// Guided "Import from Zoho" hub. Explains the export steps + links to each
// module's CSV importer. Same endpoints the manual CSV upload uses.
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Building2, Users, Target, Briefcase, UserSquare2, FileSpreadsheet, ExternalLink } from "lucide-react";

interface Module {
  label: string;
  icon: any;
  importPath: string;
  zohoModule: string;
  description: string;
}

const MODULES: Module[] = [
  {
    label: "Accounts",
    icon: Building2,
    importPath: "/accounts/import",
    zohoModule: "Accounts",
    description: "Company records — dedupes by domain.",
  },
  {
    label: "Contacts",
    icon: Users,
    importPath: "/contacts/import",
    zohoModule: "Contacts",
    description: "People records — dedupes by email.",
  },
  {
    label: "Leads",
    icon: Target,
    importPath: "/leads/import",
    zohoModule: "Leads",
    description: "Top-of-funnel — dedupes by email + LinkedIn URL.",
  },
  {
    label: "Deals",
    icon: Briefcase,
    importPath: "/deals/import",
    zohoModule: "Deals (Potentials)",
    description: "Opportunities — links accounts by name and contacts by email.",
  },
  {
    label: "Candidates",
    icon: UserSquare2,
    importPath: "/candidates/import",
    zohoModule: "Candidates (custom module)",
    description: "Staffing bench — dedupes by email. Comma-separated skills supported.",
  },
];

export default function ImportFromZohoPage() {
  return (
    <>
      <PageHeader
        title="Import from Zoho CRM"
        description="One-time or periodic CSV migration. Import each module separately, in order."
      />

      <div className="p-6 space-y-6">
        {/* Step-by-step how-to */}
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><FileSpreadsheet className="h-4 w-4"/>How it works</CardTitle></CardHeader>
          <CardContent className="text-sm">
            <ol className="list-decimal space-y-2 pl-5">
              <li>
                In Zoho: <span className="font-medium">Setup → Data Administration → Export</span>, or
                on any module list go to the <span className="font-medium">⋯</span> menu → <span className="font-medium">Export</span>.
                Pick the module (Accounts / Contacts / Leads / Deals), choose a view, and download as <span className="font-medium">CSV</span>.
              </li>
              <li>
                <span className="font-medium">Import in this order</span> for the best results —
                child records reference parents, so import parents first:
                <br />
                <span className="text-xs text-muted-foreground font-mono">
                  Accounts → Contacts → Leads → Deals → Candidates
                </span>
              </li>
              <li>
                Click <span className="font-medium">Import</span> on each module below. You'll see a preview
                of your CSV with column mappings highlighted before anything is saved.
              </li>
              <li>
                Deduplication is automatic — see each module's panel for which field is the dedupe key.
                Already-imported records are <span className="italic">skipped</span> (never duplicated).
              </li>
            </ol>
            <p className="mt-3 rounded-md bg-amber-500/10 p-3 text-xs text-amber-900 dark:text-amber-300">
              <span className="font-semibold">Tip:</span> start with a small CSV (say, 20 rows) to verify the mapping is
              right before re-uploading the full file. Each importer accepts up to 1,000 rows per upload —
              split larger files.
            </p>
          </CardContent>
        </Card>

        {/* Module cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {MODULES.map(({ label, icon: Icon, importPath, zohoModule, description }) => (
            <Card key={label} className="transition-colors hover:bg-muted/40">
              <Link to={importPath} className="block p-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{label}</span>
                      <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
                    </div>
                    <div className="mt-0.5 text-xs text-muted-foreground">Zoho module: {zohoModule}</div>
                    <p className="mt-2 text-sm text-muted-foreground">{description}</p>
                  </div>
                </div>
              </Link>
            </Card>
          ))}
        </div>

        {/* What about a live sync? */}
        <Card>
          <CardHeader><CardTitle className="text-base">Need a live two-way sync?</CardTitle></CardHeader>
          <CardContent className="text-sm text-muted-foreground space-y-2">
            <p>
              The CSV flow above is the recommended path for migration and periodic refreshes. If you need
              <span className="mx-1 font-medium">continuous, bi-directional sync</span> between Zoho and this CRM —
              for example, running both in parallel during a transition — we'd add a dedicated integration:
            </p>
            <ul className="ml-5 list-disc">
              <li>OAuth app registration in Zoho Developer Console</li>
              <li>Scheduled pulls via Zoho REST API with incremental deltas</li>
              <li>Field-level mapping configuration per module</li>
              <li>Rate-limit handling (Zoho allows ~200 req/min on paid plans)</li>
            </ul>
            <p>Let us know and we'll scope it as a phase-2 integration.</p>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
